package link.erpango.quip.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.Scaffold
import androidx.compose.material.Text
import androidx.compose.material.TopAppBar
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Share

import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import compose.icons.FontAwesomeIcons
import compose.icons.fontawesomeicons.Solid
import compose.icons.fontawesomeicons.solid.File
import compose.icons.fontawesomeicons.solid.Image
import link.erpango.quip.domain.entities.DVehicleDocument
import link.erpango.quip.features.garage.GarageViewModel
import link.erpango.quip.theme.QuipColors
import kotlinx.coroutines.launch
import coil3.compose.AsyncImage

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.AlertDialog
import androidx.compose.material.TextButton
import androidx.compose.material.TextField
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.material.LinearProgressIndicator
import androidx.compose.material.Snackbar
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import compose.icons.fontawesomeicons.Regular
import compose.icons.fontawesomeicons.solid.Question
import link.erpango.quip.utils.DatetimeUtils
import link.erpango.quip.utils.FileUtils
import link.erpango.quip.ui.components.*
import link.erpango.quip.utils.format
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.*

@Composable
fun VehicleDocumentsScreen(
    vin: String,
    viewModel: GarageViewModel,
    onBack: () -> Unit
) {
    val documents by viewModel.documents.collectAsState()
    val loadingMessage by viewModel.documentLoading.collectAsState()
    val errorMessage by viewModel.documentError.collectAsState()
    val scope = rememberCoroutineScope()
    
    // State for dialogs
    var documentToDelete by remember { mutableStateOf<DVehicleDocument?>(null) }
    var documentToRename by remember { mutableStateOf<DVehicleDocument?>(null) }
    var documentDetailsId by remember { mutableStateOf<Long?>(null) }
    var newName by remember { mutableStateOf("") }

    val documentDetails = remember(documents, documentDetailsId) {
        documents.find { it.document.id == documentDetailsId }
    }

    // Auto-dismiss error after 4 seconds
    LaunchedEffect(errorMessage) {
        if (errorMessage != null) {
            kotlinx.coroutines.delay(4000)
            viewModel.clearDocumentError()
        }
    }

    LaunchedEffect(vin) {
        viewModel.loadDocuments(vin)
    }

    if (documentToDelete != null) {
        AlertDialog(
            onDismissRequest = { documentToDelete = null },
            title = { Text(stringResource(Res.string.document_delete_title)) },
            text = { Text(stringResource(Res.string.document_delete_message, documentToDelete?.name ?: "")) },
            confirmButton = {
                TextButton(onClick = {
                    documentToDelete?.let { viewModel.deleteDocument(it) }
                    documentToDelete = null
                }) {
                    Text(stringResource(Res.string.document_delete_confirm), color = QuipColors.colorError)
                }
            },
            dismissButton = {
                TextButton(onClick = { documentToDelete = null }) {
                    Text(stringResource(Res.string.document_delete_cancel))
                }
            }
        )
    }

    if (documentToRename != null) {
        AlertDialog(
            onDismissRequest = { documentToRename = null },
            title = { Text(stringResource(Res.string.document_rename_title)) },
            text = {
                Column {
                    Text(stringResource(Res.string.document_rename_message))
                    Spacer(modifier = Modifier.height(8.dp))
                    TextField(
                        value = newName,
                        onValueChange = { newName = it },
                        singleLine = true
                    )
                }
            },
            confirmButton = {
                TextButton(onClick = {
                    documentToRename?.let { viewModel.renameDocument(it, newName) }
                    documentToRename = null
                }) {
                    Text(stringResource(Res.string.document_rename_confirm))
                }
            },
            dismissButton = {
                TextButton(onClick = { documentToRename = null }) {
                    Text(stringResource(Res.string.document_rename_cancel))
                }
            }
        )
    }
    
    if (documentDetails != null) {
        val doc = documentDetails
        QuipDialogGeneric(
            title = stringResource(Res.string.document_details_title),
            content = {
                Column(
                    modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // Update newName state when documentDetails changes
                    LaunchedEffect(doc) {
                        newName = doc.document.name
                    }



                    // Name Edit
                    QuipTextField(
                        value = newName,
                        onValueChange = { newName = it },
                        label = stringResource(Res.string.document_name),
                        modifier = Modifier.fillMaxWidth()
                    )

                    // Details
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(stringResource(Res.string.document_size), style = MaterialTheme.typography.bodySmall, color = QuipColors.colorTextSecondary)
                        Text(
                            text = "${doc.document.sizeMb.format(2)} MB",
                            style = MaterialTheme.typography.bodyLarge,
                            color = QuipColors.colorText
                        )
                    }

                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(stringResource(Res.string.document_created_at), style = MaterialTheme.typography.bodySmall, color = QuipColors.colorTextSecondary)
                        Text(
                            text = DatetimeUtils.dateToSpecificFormat(doc.document.createdAt, link.erpango.quip.QuipConstants.DateFormat.noUtcPattern1),
                            style = MaterialTheme.typography.bodyLarge,
                            color = QuipColors.colorText
                        )
                    }

                    if (doc.document.updatedAt != null) {
                        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            Text(stringResource(Res.string.document_updated_at), style = MaterialTheme.typography.bodySmall, color = QuipColors.colorTextSecondary)
                            Text(
                                text = DatetimeUtils.dateToSpecificFormat(doc.document.updatedAt!!, link.erpango.quip.QuipConstants.DateFormat.noUtcPattern1),
                                style = MaterialTheme.typography.bodyLarge,
                                color = QuipColors.colorText
                            )
                        }
                    }
                }
            },
            confirmButtonText = stringResource(Res.string.save),
            dismissButtonText = stringResource(Res.string.close),
            onConfirm = {
                if (newName.isNotBlank() && newName != doc.document.name) {
                    viewModel.renameDocument(doc.document, newName)
                }
                documentDetailsId = null
            },
            onDismiss = { documentDetailsId = null }
        )
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.document_list_title)) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Back")
                    }
                },
                backgroundColor = QuipColors.colorBackground,
                contentColor = QuipColors.black
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = {
                    scope.launch {
                        val fileData = FileUtils.pickFile(
                            allowedTypes = listOf("application/pdf", "image/png", "image/jpeg")
                        )
                        if (fileData != null) {
                            viewModel.uploadDocument(vin, fileData)
                        }
                    }
                },
                containerColor = QuipColors.colorPrimary
            ) {
                Icon(Icons.Default.Add, contentDescription = stringResource(Res.string.document_add_fab), tint = QuipColors.white)
            }
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            Column(modifier = Modifier.fillMaxSize()) {
                // Loading indicator bar
                AnimatedVisibility(
                    visible = loadingMessage != null,
                    enter = slideInVertically() + fadeIn(),
                    exit = slideOutVertically() + fadeOut()
                ) {
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp)
                    ) {
                        LinearProgressIndicator(
                            modifier = Modifier.fillMaxWidth(),
                            color = QuipColors.colorPrimary,
                            backgroundColor = QuipColors.colorPrimary.copy(alpha = 0.2f)
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            text = loadingMessage ?: "",
                            style = MaterialTheme.typography.bodySmall,
                            color = QuipColors.colorPrimary
                        )
                    }
                }

                if (documents.isEmpty() && loadingMessage == null) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(stringResource(Res.string.document_list_empty), style = MaterialTheme.typography.bodyLarge)
                    }
                } else {
                    LazyColumn(modifier = Modifier.fillMaxSize().padding(16.dp)) {
                        items(
                            items = documents,
                            key = { it.document.id }
                        ) { uiModel ->
                            val doc = uiModel.document
                            QuipDocumentItem(
                                name = doc.name,
                                fileType = doc.fileType,
                                filePath = doc.filePath,
                                sizeMb = doc.sizeMb,
                                dateFormatted = DatetimeUtils.dateToSpecificFormat(doc.createdAt, link.erpango.quip.QuipConstants.DateFormat.noUtcPattern1),
                                syncStatus = uiModel.status,
                                onShare = { viewModel.shareDocument(doc) },
                                onDelete = { documentToDelete = doc },
                                onClick = { 
                                    viewModel.openDocument(doc)
                                },
                                onLongClick = {
                                    documentDetailsId = doc.id
                                    newName = doc.name
                                },
                                onUpload = { viewModel.retryUploadDocument(doc) },
                                onDownload = { viewModel.downloadDocument(doc) },
                                onEnsureLocal = { viewModel.ensureDocumentLocal(doc) }
                            )
                        }
                    }
                }
            }

            // Error snackbar at bottom
            AnimatedVisibility(
                visible = errorMessage != null,
                enter = slideInVertically(initialOffsetY = { it }) + fadeIn(),
                exit = slideOutVertically(targetOffsetY = { it }) + fadeOut(),
                modifier = Modifier.align(Alignment.BottomCenter).padding(16.dp)
            ) {
                Snackbar(
                    action = {
                        TextButton(onClick = { viewModel.clearDocumentError() }) {
                            Text(stringResource(Res.string.ok), color = QuipColors.white)
                        }
                    },
                    backgroundColor = QuipColors.colorError
                ) {
                    Text(errorMessage ?: "", color = QuipColors.white)
                }
            }
        }
    }
}


// Removed local DocumentItem as it is replaced by QuipDocumentItem

